import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { storage, STORAGE_KEYS } from '@utils/storage';
import type { AuthTokens, ApiResponse, ApiError, User } from '@/types';

type FailedQueueItem = {
  requestConfig: InternalAxiosRequestConfig & { _retry?: boolean };
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
};

type RefreshConcurrencyState = {
  isRefreshing: boolean;
  failedQueue: FailedQueueItem[];
};

function getGlobalRefreshState(): RefreshConcurrencyState {
  const anyGlobal = globalThis as any;
  const existing = anyGlobal.__lezzetyolda_refresh_state__ as RefreshConcurrencyState | undefined;
  if (existing) return existing;
  const created: RefreshConcurrencyState = { isRefreshing: false, failedQueue: [] };
  anyGlobal.__lezzetyolda_refresh_state__ = created;
  return created;
}

function normalizePathnameFromAxiosUrl(rawUrl: string | undefined): string {
  if (!rawUrl) return '/';
  try {
    if (rawUrl.startsWith('http://') || rawUrl.startsWith('https://')) {
      return new URL(rawUrl).pathname || '/';
    }
  } catch {
    // ignore
  }
  return rawUrl.startsWith('/') ? rawUrl : `/${rawUrl}`;
}

function hasActiveAuthSession(): boolean {
  const tokens = storage.get<AuthTokens>(STORAGE_KEYS.AUTH_TOKENS);
  const user = storage.get<User>(STORAGE_KEYS.USER_DATA);
  return Boolean(tokens?.access_token && user);
}

function isRestaurantZoneCheckPath(pathname: string): boolean {
  return (
    /^\/catalog\/public\/restaurants\/[^/]+\/zones\/check-address$/.test(pathname) ||
    /^\/geo\/restaurants\/[^/]+\/check-delivery-zone$/.test(pathname) ||
    /^\/catalog\/restaurants\/[^/]+\/zones\/check-address$/.test(pathname)
  );
}

function shouldAttachGuestIdToRequest(url: string | undefined): boolean {
  const pathname = normalizePathnameFromAxiosUrl(url);
  if (pathname === '/cart' || pathname.startsWith('/cart/')) {
    return true;
  }
  if (pathname === '/orders' || pathname === '/payments/initialize') {
    return true;
  }
  if (isRestaurantZoneCheckPath(pathname)) {
    return true;
  }
  return false;
}

function isAnonymousSafeRequest(url: string | undefined): boolean {
  const pathname = normalizePathnameFromAxiosUrl(url);
  if (
    pathname === '/geo/check-zone' ||
    pathname.startsWith('/geo/restaurants/') ||
    isRestaurantZoneCheckPath(pathname)
  ) {
    return true;
  }
  return shouldAttachGuestIdToRequest(url);
}

const PUBLIC_ROUTES = [
  '/',
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/complete-registration',
  '/accept-terms',
  '/checkout',
  '/checkout/confirmation',
  '/checkout/result',
  '/siparis-takip',
];

function shouldAttachRefreshTokenToRequest(url: string | undefined): boolean {
  const pathname = normalizePathnameFromAxiosUrl(url);
  return pathname === '/auth/logout';
}

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: import.meta.env.VITE_API_URL || '/api',
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private applyAuthTokensToAxiosDefaults(tokens: AuthTokens) {
    this.client.defaults.headers.common.Authorization = `Bearer ${tokens.access_token}`;
    delete (this.client.defaults.headers.common as any)['x-refresh-token'];
  }

  private persistAuthTokens(tokens: AuthTokens) {
    storage.set(STORAGE_KEYS.AUTH_TOKENS, tokens);
    this.applyAuthTokensToAxiosDefaults(tokens);
    window.dispatchEvent(new CustomEvent('auth:tokens', { detail: { tokens } }));
  }

  private getOrCreateGuestId(): string {
    let guestId = storage.get<string>(STORAGE_KEYS.GUEST_ID);
    if (!guestId) {
      guestId = uuidv4();
      storage.set(STORAGE_KEYS.GUEST_ID, guestId);
    }
    return guestId;
  }

  private stripStaleAuthTokensOnly() {
    storage.remove(STORAGE_KEYS.AUTH_TOKENS);
    storage.remove(STORAGE_KEYS.USER_DATA);
    delete this.client.defaults.headers.common.Authorization;
    delete (this.client.defaults.headers.common as any)['x-refresh-token'];
  }

  private clearLocalAuthSession() {
    this.stripStaleAuthTokensOnly();
    window.dispatchEvent(new CustomEvent('auth:clear'));
  }

  private retryRequestAsGuest(
    originalRequest: InternalAxiosRequestConfig & { _retry?: boolean },
  ) {
    this.stripStaleAuthTokensOnly();
    originalRequest._retry = true;
    originalRequest.headers = originalRequest.headers ?? {};
    delete (originalRequest.headers as any).Authorization;
    delete (originalRequest.headers as any)['x-refresh-token'];

    const guestId = this.getOrCreateGuestId();
    if (guestId) {
      originalRequest.headers['x-guest-id'] = guestId;
    }

    return this.client(originalRequest);
  }

  private handleAuthFailureAndLogout(error: unknown) {
    this.clearLocalAuthSession();

    const isPublicRoute = PUBLIC_ROUTES.includes(window.location.pathname);
    const isRestaurantPage = /^\/restaurant\/[^/]+$/.test(window.location.pathname);

    if (!isPublicRoute && !isRestaurantPage) {
      window.location.href = '/login';
      return;
    }

    void error;
  }

  private processFailedQueue(error: unknown, newTokens: AuthTokens | null) {
    const refreshState = getGlobalRefreshState();
    const queue = refreshState.failedQueue;
    refreshState.failedQueue = [];

    for (const queued of queue) {
      if (error) {
        queued.reject(error);
        continue;
      }

      const accessToken = newTokens?.access_token;
      const refreshToken = newTokens?.refresh_token;
      if (!accessToken || !refreshToken) {
        queued.reject(new Error('Missing refreshed auth tokens'));
        continue;
      }

      queued.requestConfig.headers = queued.requestConfig.headers ?? {};
      queued.requestConfig.headers.Authorization = `Bearer ${accessToken}`;
      delete (queued.requestConfig.headers as any)['x-refresh-token'];
      queued.resolve(this.client(queued.requestConfig));
    }
  }

  private setupInterceptors() {
    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        config.headers = config.headers ?? {};
        const tokens = storage.get<AuthTokens>(STORAGE_KEYS.AUTH_TOKENS);
        const useAuthenticatedSession = hasActiveAuthSession() && Boolean(tokens?.access_token);

        if (useAuthenticatedSession) {
          config.headers.Authorization = `Bearer ${tokens!.access_token}`;
          if (shouldAttachRefreshTokenToRequest(config.url)) {
            config.headers['x-refresh-token'] = tokens!.refresh_token;
          } else {
            delete (config.headers as any)['x-refresh-token'];
          }
          delete (config.headers as any)['x-guest-id'];
        } else {
          delete (config.headers as any).Authorization;
          delete (config.headers as any)['x-refresh-token'];

          if (shouldAttachGuestIdToRequest(config.url)) {
            const guestId = this.getOrCreateGuestId();
            config.headers['x-guest-id'] = guestId;
          } else {
            delete (config.headers as any)['x-guest-id'];
          }
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    this.client.interceptors.response.use(
      (response) => {
        const guestIdHeader = response.headers['x-guest-id'];
        if (guestIdHeader && typeof guestIdHeader === 'string') {
          if (!hasActiveAuthSession()) {
            storage.set(STORAGE_KEYS.GUEST_ID, guestIdHeader);
          }
        }

        return response;
      },
      async (error: AxiosError<ApiError>) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

        const originalPathname = normalizePathnameFromAxiosUrl(originalRequest?.url);
        if (originalPathname.startsWith('/auth/')) {
          return Promise.reject(error);
        }

        if (error.response?.status === 401 && !originalRequest._retry) {
          if (isAnonymousSafeRequest(originalRequest?.url)) {
            if (shouldAttachGuestIdToRequest(originalRequest?.url) && hasActiveAuthSession()) {
              return this.retryRequestAsGuest(originalRequest);
            }
            return Promise.reject(error);
          }

          const refreshState = getGlobalRefreshState();
          const tokens = storage.get<AuthTokens>(STORAGE_KEYS.AUTH_TOKENS);

          if (!tokens?.refresh_token) {
            if (shouldAttachGuestIdToRequest(originalRequest?.url)) {
              return this.retryRequestAsGuest(originalRequest);
            }
            this.handleAuthFailureAndLogout(error);
            return Promise.reject(error);
          }

          originalRequest._retry = true;

          if (refreshState.isRefreshing) {
            return new Promise((resolve, reject) => {
              refreshState.failedQueue.push({ requestConfig: originalRequest, resolve, reject });
            });
          }

          refreshState.isRefreshing = true;
          try {
            const refreshedTokens = await this.refreshTokenPair(tokens.refresh_token);
            this.persistAuthTokens(refreshedTokens);
            this.processFailedQueue(null, refreshedTokens);

            originalRequest.headers = originalRequest.headers ?? {};
            originalRequest.headers.Authorization = `Bearer ${refreshedTokens.access_token}`;
            delete (originalRequest.headers as any)['x-refresh-token'];

            return this.client(originalRequest);
          } catch (refreshError) {
            this.processFailedQueue(refreshError, null);
            if (shouldAttachGuestIdToRequest(originalRequest?.url)) {
              return this.retryRequestAsGuest(originalRequest);
            }
            this.handleAuthFailureAndLogout(refreshError);
            return Promise.reject(refreshError);
          } finally {
            refreshState.isRefreshing = false;
          }
        }

        return Promise.reject(error);
      }
    );
  }

  private async refreshTokenPair(refreshToken: string): Promise<AuthTokens> {
    const apiBaseUrl = import.meta.env.VITE_API_URL || '/api';
    const response = await axios.post<ApiResponse<AuthTokens>>(`${apiBaseUrl}/auth/refresh`, {
      refresh_token: refreshToken,
    });

    const newTokens = response.data.data;
    if (!newTokens?.access_token || !newTokens?.refresh_token) {
      throw new Error('Refresh response did not include token pair');
    }
    return newTokens;
  }

  public get<T>(url: string, config = {}) {
    return this.client.get<ApiResponse<T>>(url, config);
  }

  public post<T>(url: string, data?: unknown, config = {}) {
    return this.client.post<ApiResponse<T>>(url, data, config);
  }

  public put<T>(url: string, data?: unknown, config = {}) {
    return this.client.put<ApiResponse<T>>(url, data, config);
  }

  public patch<T>(url: string, data?: unknown, config = {}) {
    return this.client.patch<ApiResponse<T>>(url, data, config);
  }

  public delete<T>(url: string, config = {}) {
    return this.client.delete<ApiResponse<T>>(url, config);
  }
}

export const apiClient = new ApiClient();
